import { useLocation, useNavigate } from "react-router-dom";
import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import useSound from "use-sound";
import "./BottomNavigationBar.css";
import useSoundStore from "../stores/useSoundStore";
import posthog from "posthog-js";
import { centerMapOnCityHall } from "../scenes/map/mapCamera";

// Локальні іконки
const iconCoins = "/ui/navigation-icons/hell-of-flame-icon.svg";
const iconBuildings = "/ui/navigation-icons/shop.svg";
const iconShell = "/ui/navigation-icons/shell.png";
const iconShellActive = "/ui/navigation-icons/shell-active.png";
const iconMenu = "/ui/navigation-icons/menu.svg";
const iconMap = "/ui/navigation-icons/map.svg";

interface NavItem {
  path: string;
  icon: string; // URL іконки
  isCenter?: boolean; // Центральна кнопка (мапа)
}

export default function BottomNavigationBar() {
  const { soundsEnabled } = useSoundStore();
  const [playNavTap] = useSound('/game-assets/sounds/navigator-tap.mp3', {
    volume: 0.08,
    soundEnabled: soundsEnabled,
  });
  const location = useLocation();
  const navigate = useNavigate();
  const [swipeHighlightedIndex, setSwipeHighlightedIndex] = useState<number | null>(null);
  const [isSwipeActive, setIsSwipeActive] = useState(false);
  const [swipePosition, setSwipePosition] = useState<number | null>(null); // Позиція X для плавного руху виділення
  const navBarRef = useRef<HTMLElement | null>(null);
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const isSwiping = useRef(false);
  const iconUpdateTimeoutRef = useRef<number | null>(null); // Для відкладеного оновлення іконок
  const rafIdRef = useRef<number | null>(null); // Для requestAnimationFrame
  const lastHighlightedIndexRef = useRef<number | null>(null); // Кеш останнього індексу для уникнення зайвих оновлень

  // Static routes — memoize so touch handlers are not recreated every render.
  const navItems: NavItem[] = useMemo(() => [
    { path: "/shop", icon: iconBuildings }, // Магазин - зліва
    { path: "/top", icon: iconCoins }, // Top-100 - після магазину
    { path: "/tap", icon: iconShell }, // Мюшля - TapScreen
    { path: "/map", icon: iconMap }, // Центральна позиція, але не завжди активна
    { path: "/menu", icon: iconMenu },
  ], []);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const handleNavClick = (path: string) => {
    playNavTap();
    posthog.capture('nav_bar_clicked', { path });
    if (path === "/map" && location.pathname === "/map") {
      centerMapOnCityHall();
      return;
    }
    navigate(path);
  };

  const handleTouchStart = useCallback((e: TouchEvent) => {
    const touch = e.touches[0];
    touchStartX.current = touch.clientX;
    touchStartY.current = touch.clientY;
    isSwiping.current = false;
    
    // Визначаємо початкову позицію та найближчу кнопку
    if (!navBarRef.current) return;
    const navBarRect = navBarRef.current.getBoundingClientRect();
    const relativeX = touch.clientX - navBarRect.left;
    
    setSwipePosition(relativeX);
    
    // Знаходимо найближчу кнопку
    const navBarWidth = navBarRect.width;
    const iconCount = navItems.length;
    const iconWidth = navBarWidth / iconCount;
    
    let closestIndex = 0;
    let minDistance = Infinity;
    
    for (let i = 0; i < iconCount; i++) {
      const buttonCenterX = (i + 0.5) * iconWidth;
      const distance = Math.abs(relativeX - buttonCenterX);
      if (distance < minDistance) {
        minDistance = distance;
        closestIndex = i;
      }
    }
    
    setSwipeHighlightedIndex(closestIndex);
  }, [navItems.length]);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (touchStartX.current === null || touchStartY.current === null || !navBarRef.current) return;
    
    const touch = e.touches[0];
    const deltaX = Math.abs(touch.clientX - touchStartX.current);
    const deltaY = Math.abs(touch.clientY - touchStartY.current);
    
    // Якщо рух переважно горизонтальний - це свайп
    if (deltaX > 10 && deltaX > deltaY) {
      isSwiping.current = true;
      setIsSwipeActive(true); // Позначаємо, що свайп активний
      
      // Перевіряємо, чи подія cancelable перед викликом preventDefault
      if (e.cancelable) {
        e.preventDefault(); // Запобігаємо скролу
      }
      
      const navBarRect = navBarRef.current.getBoundingClientRect();
      const relativeX = touch.clientX - navBarRect.left;
      const clampedX = Math.max(0, Math.min(relativeX, navBarRect.width));
      
      // КРИТИЧНЕ: Оновлюємо позицію виділення синхронно через requestAnimationFrame
      // Це забезпечує плавний рух без лагів
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
      
      rafIdRef.current = requestAnimationFrame(() => {
        setSwipePosition(clampedX);
        rafIdRef.current = null;
      });
      
      // НЕКРИТИЧНЕ: Відкладаємо оновлення іконок (збільшення) на наступний кадр
      // Це дозволяє руху виділення бути пріоритетним
      if (iconUpdateTimeoutRef.current !== null) {
        clearTimeout(iconUpdateTimeoutRef.current);
      }
      
      iconUpdateTimeoutRef.current = window.setTimeout(() => {
        // Знаходимо найближчу кнопку для відображення виділення
        const navBarWidth = navBarRect.width;
        const iconCount = navItems.length;
        const iconWidth = navBarWidth / iconCount;
        
        let closestIndex = 0;
        let minDistance = Infinity;
        
        for (let i = 0; i < iconCount; i++) {
          const buttonCenterX = (i + 0.5) * iconWidth;
          const distance = Math.abs(clampedX - buttonCenterX);
          if (distance < minDistance) {
            minDistance = distance;
            closestIndex = i;
          }
        }
        
        // Оновлюємо тільки якщо індекс змінився (уникаємо зайвих ре-рендерів)
        if (lastHighlightedIndexRef.current !== closestIndex) {
          lastHighlightedIndexRef.current = closestIndex;
          setSwipeHighlightedIndex(closestIndex);
        }
        iconUpdateTimeoutRef.current = null;
      }, 0); // Виконується в наступному циклі подій
    }
  }, [navItems.length]);

  const handleTouchEnd = useCallback(async () => {
    // Очищаємо таймери та анімації
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    if (iconUpdateTimeoutRef.current !== null) {
      clearTimeout(iconUpdateTimeoutRef.current);
      iconUpdateTimeoutRef.current = null;
    }
    
    if (isSwiping.current && swipeHighlightedIndex !== null) {
      // Якщо був свайп - натискаємо на останню виділену іконку
      const item = navItems[swipeHighlightedIndex];
      if (item) {
        playNavTap();
        posthog.capture('nav_bar_clicked', { path: item.path, method: 'swipe' });
        navigate(item.path);
      }
    }
    
    // Скидаємо стан
    setSwipeHighlightedIndex(null);
    setSwipePosition(null);
    setIsSwipeActive(false);
    lastHighlightedIndexRef.current = null;
    touchStartX.current = null;
    touchStartY.current = null;
    isSwiping.current = false;
  }, [swipeHighlightedIndex, navItems, playNavTap, navigate]);

  // Попереднє завантаження всіх іконок для уникнення лагів
  useEffect(() => {
    const preloadImages = [
      iconCoins,
      iconBuildings,
      iconShell,
      iconShellActive,
      iconMenu,
      iconMap,
    ];
    
    preloadImages.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  // Додаємо нативні обробники подій з passive: false
  useEffect(() => {
    const navElement = navBarRef.current;
    if (!navElement) return;

    // Додаємо обробники з passive: false для touchmove, щоб можна було викликати preventDefault
    navElement.addEventListener('touchstart', handleTouchStart, { passive: true });
    navElement.addEventListener('touchmove', handleTouchMove, { passive: false });
    navElement.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      navElement.removeEventListener('touchstart', handleTouchStart);
      navElement.removeEventListener('touchmove', handleTouchMove);
      navElement.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);


  return (
    <>
      <nav 
        className="bottom-navigation-bar"
        ref={navBarRef}
      >
      {/* Плавне виділення, що рухається за пальцем - позаду іконок */}
      {isSwipeActive && swipePosition !== null && (
        <div
          className="swipe-highlight-indicator pointer-events-none absolute top-1/2 z-[1] h-[60px] w-[60px] -translate-x-1/2 -translate-y-1/2 rounded-full border border-solid border-navigation-active-border bg-[image:var(--gradient-navigation-active)] shadow-navigation-active transition-none"
          style={{
            left: `${swipePosition}px`, // dynamic swipe X
          }}
        />
      )}
      {navItems.map((item, index) => {
        const active = isActive(item.path);
        const isCenter = index === 2; // Ракушка (tap) завжди в центральній позиції (індекс 2)
        // Для мюшлі використовуємо різні іконки для активної та неактивної
        const iconSrc = isCenter && active ? iconShellActive : item.icon;
        // Виділення під час свайпу - для збільшення всіх іконок коли вони найближчі
        const isSwipeHighlighted = isSwipeActive && swipeHighlightedIndex === index;
        // Центральна кнопка завжди велика, активна кнопка також велика з круглим фоном
        return (
          <button
            key={index}
            data-nav-path={item.path}
            className={`nav-item relative z-[2] ${active && !isSwipeActive ? "active" : ""} ${isCenter ? "center-button" : ""} ${isSwipeHighlighted ? "swipe-highlighted" : ""}`}
            onClick={() => {
              handleNavClick(item.path);
            }}
          >
            <div className={`nav-icon-wrapper ${active && !isSwipeActive ? "active-icon-wrapper" : ""} ${isCenter ? "center-icon-wrapper" : ""} ${isSwipeHighlighted ? "swipe-highlighted-wrapper" : ""}`}>
              <img 
                src={iconSrc} 
                alt=""
                className="nav-icon"
              />
            </div>
          </button>
        );
      })}
    </nav>
    </>
  );
}

